import { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "./Common";
import { TRADES, CITIES } from "@/lib/hz";
import { sendContact } from "@/lib/api";

const EMPTY = { name: "", email: "", phone: "", role: "cliente", trade: "", city: "", message: "" };

export const ContactForm = () => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await sendContact(f);
      setDone(true);
      setF(EMPTY);
      toast.success("Solicitud enviada. Te contactaremos muy pronto.");
    } catch {
      toast.error("Revisa los datos: nombre, email válido y un mensaje.");
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div data-testid="contact-success" className="rounded-3xl border border-emerald-200 bg-emerald-50 p-8 text-center">
        <CheckCircle2 className="mx-auto size-10 text-emerald-600" />
        <div className="mt-3 font-heading text-xl font-bold text-slate-900">¡Recibido!</div>
        <p className="mt-1 text-sm text-slate-600">Nuestro equipo de atención al cliente te responderá en menos de 24 h.</p>
        <Button variant="outline" className="mt-5" onClick={() => setDone(false)} data-testid="contact-again-btn">Enviar otra solicitud</Button>
      </div>
    );

  return (
    <form onSubmit={submit} data-testid="contact-form" className="space-y-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
      <div className="flex gap-2">
        {[["cliente", "Necesito un servicio"], ["profesional", "Soy profesional"]].map(([v, l]) => (
          <button key={v} type="button" data-testid={`contact-role-${v}`} onClick={() => setF({ ...f, role: v })}
            className={`h-11 flex-1 rounded-xl border-2 text-sm font-semibold transition-colors ${f.role === v ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>{l}</button>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Nombre"><Input required value={f.name} onChange={set("name")} data-testid="contact-name-input" className="h-11" /></Field>
        <Field label="Email"><Input required type="email" value={f.email} onChange={set("email")} data-testid="contact-email-input" className="h-11" /></Field>
        <Field label="Teléfono"><Input value={f.phone} onChange={set("phone")} data-testid="contact-phone-input" className="h-11" /></Field>
        <Field label="Ciudad">
          <Select value={f.city} onValueChange={set("city")}>
            <SelectTrigger className="h-11" data-testid="contact-city-select"><SelectValue placeholder="Elige ciudad" /></SelectTrigger>
            <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
      <Field label={f.role === "profesional" ? "Tu oficio" : "¿Qué necesitas?"}>
        <Select value={f.trade} onValueChange={set("trade")}>
          <SelectTrigger className="h-11" data-testid="contact-trade-select"><SelectValue placeholder="Elige un oficio" /></SelectTrigger>
          <SelectContent>{TRADES.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Mensaje"><Textarea required rows={4} value={f.message} onChange={set("message")} data-testid="contact-message-input" placeholder="Cuéntanos qué necesitas…" /></Field>
      <Button type="submit" size="lg" className="w-full rounded-xl" disabled={busy} data-testid="contact-submit-btn">
        {busy ? <Loader2 className="animate-spin" /> : <Send />} Enviar solicitud
      </Button>
    </form>
  );
};
